/***
 *
 *   FILE UPLOADER
 *   Drag & drop file upload component
 *   Can upload multiple files
 *   Includes fallback for older browser
 *
 *   PROPS
 *   name: input name
 *   max: max number of files
 *   maxFileSize: limit size of file in mb§
 *   accept: array of filetypes to accept, eg. ['jpg', 'gif']
 *
 **********/

import type React from 'react';
import { useState, useRef } from 'react';
import { Icon, ClassHelper } from 'components/lib';
import { Label } from '../label/label';
import { Error } from '../error/error';
import Style from './file.tailwind.js';
import { UMButton } from '@urbanmetrix/um-kit';

interface StoredFile {
  name: string;
  data?: File;
  url?: string;
  size: number;
  type: string;
}

interface FileInputProps {
  errorMessage?: string;
  value?: StoredFile[];
  max?: number;
  accept?: string[];
  maxFileSize?: number;
  onChange: (name: string | undefined, files: StoredFile[], valid?: boolean) => void;
  name?: string;
  style?: React.CSSProperties;
  label?: string;
  required?: boolean;
}

export function FileInput(props: FileInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [valid, setValid] = useState<boolean | undefined>(undefined);
  const [error, setError] = useState(props.errorMessage || 'Please select a file');

  function validate(files: FileList) {
    const fileStore: StoredFile[] = [];

    // check for max files
    if (files.length + (props.value?.length || 0) > (props.max ?? Infinity)) {
      setValid(false);
      setError(`Maximum of ${props.max} file`);
      return;
    } else {
      // check files exist
      if (files.length) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const type = file.type.substring(file.type.indexOf('/') + 1).toString();

          // validate file type
          if (props.accept?.length && !props.accept.includes(type)) {
            setValid(false);
            setError('.' + file.type.substring(file.type.indexOf('/') + 1) + ' files are not allowed');
          }

          // validate file size (in mb)
          else if (props.maxFileSize && file.size > 1048576 * props.maxFileSize) {
            setValid(false);
            setError(`Max filesize: ${props.maxFileSize}mb`);
          } else {
            // store the file in form store
            setValid(true);
            fileStore.push({
              name: file.name,
              data: file,
              url: URL.createObjectURL(file),
              size: file.size,
              type: file.type,
            });
          }
        }

        props.onChange(props.name, fileStore, valid);
      }
    }
  }

  function deleteFile(file: StoredFile) {
    if (fileInputRef.current) fileInputRef.current.value = '';

    props.onChange(
      props.name,
      [
        {
          name: file.name,
          size: file.size,
          type: file.type,
        },
      ],
      true
    );
  }

  function onDrag(e: React.DragEvent<HTMLElement> | React.MouseEvent<HTMLElement>, state: boolean) {
    e.preventDefault();
    e.stopPropagation();
    setDragging(state);
  }

  const fileStyle = ClassHelper(Style, {
    dropzone: true,
    dragging: dragging,
    error: valid === false ? true : false,
    success: props.value?.length && valid === true,
  });

  return (
    <div
      className={Style.file}
      // this line was added by Huseyin
      style={props.style}
    >
      {props.label && <Label text={props.label} required={props.required} for={props.name} />}

      <div
        className={fileStyle}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => onDrag(e, true)}
        onDragEnter={(e) => onDrag(e, true)}
        onDragLeave={(e) => onDrag(e, false)}
        onDrop={(e) => {
          onDrag(e, false);
          validate(e.dataTransfer.files);
        }}
      >
        {/* fallback for old browsers */}
        <input
          type="file"
          className={Style.legacyInput}
          ref={fileInputRef}
          onChange={() => {
            if (fileInputRef.current?.files) validate(fileInputRef.current.files);
          }}
        />

        <div className={Style.label}>
          <Icon image={props.value?.length ? 'check' : 'upload'} className={Style.labelIcon} />

          {props.value?.length ? (
            <FileList files={props.value} delete={deleteFile} />
          ) : (
            <div className={Style.labelText}>Drop file here</div>
          )}
        </div>
      </div>

      {valid === false && <Error message={error} />}
    </div>
  );
}

interface FileListProps {
  files?: StoredFile[];
  delete: (file: StoredFile) => void;
}

function FileList(props: FileListProps) {
  if (!props.files?.length) return false;

  return (
    <div>
      {props.files.map((file) => {
        return (
          <div key={file.name} className={Style.fileListItem}>
            <span>{file.name}</span>

            <UMButton
              icon="x"
              color="#d95565"
              size={13}
              className={Style.fileListButton}
              onClick={() => props.delete(file)}
            />
          </div>
        );
      })}
    </div>
  );
}
